if (!customElements.get('nyvolt-home-reels')) {
  customElements.define('nyvolt-home-reels', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.track = this.querySelector('.nv-reels__track');
      this.cards = Array.from(this.querySelectorAll('.nv-reel'));
      this.classList.remove('nv-reels--buttons');
      this.cards.forEach(card => { card.hidden = false; });
      if (!this.track || !this.cards.length) return;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.ratios = new Map();
      this.manualPaused = new Set();
      const on = (target, type, fn, options = {}) => target.addEventListener(type, fn, {...options, signal:this.events.signal});
      if ('IntersectionObserver' in window) {
        this.posterObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            this.loadPoster(entry.target);
            this.posterObserver.unobserve(entry.target);
          });
        }, {rootMargin:'250px 0px'});
      }
      this.cards.forEach(card => {
        if (this.posterObserver) this.posterObserver.observe(card);
        else this.loadPoster(card);
        const video = card.querySelector('video');
        const button = card.querySelector('[data-reel-toggle]');
        video.controls = false;
        video.muted = true;
        button.hidden = false;
        on(video, 'play', () => this.update(card));
        on(video, 'pause', () => this.update(card));
        on(video, 'error', () => { this.manualPaused.add(card); this.update(card); });
        on(button, 'click', () => {
          if (!video.paused) { this.manualPaused.add(card); video.pause(); }
          else { this.manualPaused.delete(card); this.selected = card; this.play(card); }
        });
        on(card, 'pointerenter', event => { if (event.pointerType === 'mouse' && !this.reduced.matches && !navigator.connection?.saveData) { this.selected = card; this.sync(); } });
        this.update(card);
      });
      this.querySelector('.nv-reels__arrows').hidden = false;
      on(this.querySelector('[data-reels-next]'), 'click', () => this.move(1));
      on(this.querySelector('[data-reels-previous]'), 'click', () => this.move(-1));
      on(this.track, 'scroll', () => { clearTimeout(this.scrollTimer); this.scrollTimer = setTimeout(() => this.updateArrows(), 80); }, {passive:true});
      on(document, 'visibilitychange', () => this.sync());
      on(this.reduced, 'change', () => this.sync());
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => this.ratios.set(entry.target, entry.intersectionRatio));
          this.sync();
        }, {threshold:[0,0.3,0.55,0.75,1]});
        this.cards.forEach(card => this.observer.observe(card));
      }
      this.resize = new ResizeObserver(() => this.updateArrows());
      this.resize.observe(this.track);
      this.updateArrows();
    }
    sync() {
      const eligible = this.cards.filter(card => (this.ratios.get(card) || 0) >= .55);
      const chosen = eligible.includes(this.selected) ? this.selected : eligible[0];
      if (document.hidden || this.reduced.matches || navigator.connection?.saveData || !chosen) {
        this.cards.forEach(card => card.querySelector('video').pause());
        return;
      }
      if (!this.manualPaused.has(chosen)) this.play(chosen);
      else this.cards.forEach(card => card.querySelector('video').pause());
    }
    loadPoster(card) {
      const video = card.querySelector('[data-reel-video]');
      if (!video?.dataset.poster) return;
      video.poster = video.dataset.poster;
      delete video.dataset.poster;
    }
    play(card) {
      this.loadPoster(card);
      this.cards.forEach(other => { if (other !== card) other.querySelector('video').pause(); });
      const video = card.querySelector('video');
      const sources = Array.from(video.querySelectorAll('source[data-src]'));
      sources.forEach(source => { source.src = source.dataset.src; delete source.dataset.src; });
      if (sources.length) video.load();
      if (video.paused) video.play().catch(() => this.update(card));
    }
    update(card) {
      const video = card.querySelector('video');
      const button = card.querySelector('[data-reel-toggle]');
      card.classList.toggle('is-playing', !video.paused);
      const label = video.paused ? this.dataset.playLabel : this.dataset.pauseLabel;
      button.setAttribute('aria-label', label + ' — ' + button.dataset.modelName);
      button.setAttribute('aria-pressed', String(!video.paused));
    }
    move(direction) {
      const left = this.track.getBoundingClientRect().left;
      const current = this.cards.reduce((best,card,index) => Math.abs(card.getBoundingClientRect().left - left - parseFloat(getComputedStyle(this.track).paddingLeft)) < best.distance ? {index,distance:Math.abs(card.getBoundingClientRect().left - left - parseFloat(getComputedStyle(this.track).paddingLeft))} : best, {index:0,distance:Infinity}).index;
      const next = this.cards[Math.max(0,Math.min(this.cards.length-1,current+direction))];
      this.track.scrollTo({left:this.track.scrollLeft + next.getBoundingClientRect().left - left - parseFloat(getComputedStyle(this.track).paddingLeft),behavior:this.reduced.matches?'auto':'smooth'});
      this.selected = next;
    }
    updateArrows() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.querySelector('[data-reels-previous]').disabled = this.track.scrollLeft < 4;
      this.querySelector('[data-reels-next]').disabled = this.track.scrollLeft > max - 4;
    }
    disconnectedCallback() {
      this.cards?.forEach(card => card.querySelector('video').pause());
      this.events?.abort(); this.events = null;
      this.observer?.disconnect(); this.posterObserver?.disconnect(); this.resize?.disconnect(); clearTimeout(this.scrollTimer);
    }
  });
}


if (!customElements.get('nyvolt-instagram-feed')) {
  customElements.define('nyvolt-instagram-feed', class extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.observer.disconnect();
            this.load();
          }
        }, {rootMargin: '600px 0px'});
        this.observer.observe(this);
      } else this.load();
    }
    load() {
      if (window.instgrm?.Embeds) {
        window.instgrm.Embeds.process();
        return;
      }
      let script = document.getElementById('nyvolt-instagram-embed');
      if (!script) {
        script = document.createElement('script');
        script.id = 'nyvolt-instagram-embed';
        script.src = 'https://www.instagram.com/embed.js';
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', () => window.instgrm?.Embeds?.process(), {once: true});
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.started = false;
    }
  });
}
